import { toast } from "@/components/common/Toast/Toast"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Fragment, useMemo, type ReactNode } from "react"
import {
  useApplyStatementPoolScope,
  useCreateStatement,
  useUpdateStatementScope,
} from "../../../../../api/declarationsApi"
import type {
  CandidateArticle,
  IssuerScopeProposal,
} from "../../../../../api/types"
import type { Verdict } from "../../studio/studioTypes"
import type { FilterToken } from "../../studio/TableSearch"
import { deriveGeltung, type Geltung } from "../mapping/geltungMapping"
import { verdictLabelKey } from "../mapping/verdictDisplay"
import {
  CountPill,
  geltungSentence,
  headSubjectCount,
  headTokenOrder,
  headVerb,
  verdictSentence,
  type HeadToken,
} from "../reviewmask/HeaderSentence"
import { uncoveredArticleNumbers } from "../reviewmask/RemainderCard"
import { VerdictSelect } from "../reviewmask/VerdictSelect"
import type { StatementCard } from "../useStudioV2State"
import {
  ArticleBrowser,
  type BrowserStatement,
  type BrowserView,
} from "./ArticleBrowser"
import {
  needsSeveralManufacturersHint,
  SeveralManufacturersHint,
} from "./IssuerScopeNote"
import { issuerSuggestion, searchSuggestions } from "./suggestions"

/** The focused step that takes over the review-mask pane (the PDF stays
 *  visible beside it): the article browser, for one statement's scope or
 *  for the articles no statement of the Verordnung covers yet. */
export type ArticleSheet =
  | {
      kind: "scope"
      cardId: string
      view: BrowserView
      /** The card's Geltung kind at the moment the sheet was opened, so the
       *  browser can show the "material" mapping hint. Undefined for older
       *  callers / the never-scope_all remainder view. */
      geltung?: Geltung
    }
  | { kind: "remainder"; view: BrowserView }

type Translate = (
  key: TranslationKeys,
  v?: Record<string, string | number>,
) => string

/** The card's header sentence without its controls: count, verb, Geltung,
 *  verdict, in the language's own token order. */
function StatementSentence({
  card,
  total,
  translate,
}: {
  card: StatementCard
  total: number
  translate: Translate
}) {
  const geltung = deriveGeltung({
    scopeAll: card.scopeAll,
    selectedCount: card.articleNumbers.length,
    unresolvedRefs: card.unresolvedRefs,
    inquiryOpen: false,
  })
  const count = card.scopeAll ? total : card.articleNumbers.length
  const unclear = geltung === "ask" && !card.scopeAll && count === 0
  const tokens: Record<HeadToken, ReactNode> = {
    count: (
      <CountPill
        count={count}
        total={total}
        all={card.scopeAll}
        unclear={unclear}
        quiet={!card.scopeAll && !unclear && count === 0}
        translate={translate}
      />
    ),
    verb: (
      <span className="sv2-hw">
        {headVerb(
          headSubjectCount({ count, total, all: card.scopeAll, unclear }),
          translate,
        )}
      </span>
    ),
    geltung: (
      <span className="sv2-hw">{geltungSentence(geltung, translate)}</span>
    ),
    verdict: (
      <span className="sv2-hw">{verdictSentence(card.verdict, translate)}</span>
    ),
  }
  return (
    <>
      {headTokenOrder(translate).map((token) => (
        <Fragment key={token}>{tokens[token]}</Fragment>
      ))}
    </>
  )
}

export interface ArticleBrowserSheetProps {
  declId: string
  framework: string
  sheet: ArticleSheet
  /** The active Verordnung's cards, excluded ones included (they neither
   *  cover articles nor appear as a quick view). */
  cards: StatementCard[]
  pool: CandidateArticle[]
  /** MC_ISSUER_SCOPE_PROPOSAL (TUR-1720): the declaration's issuer proposal.
   *  A match is the statement's FIRST suggestion (index 0, which
   *  `IssuerScopeNote`'s card button opens); no match on a pool of several
   *  manufacturers is a note above the suggestions. Null/omitted with the
   *  flag off: the suggestions are exactly the document's own. */
  issuerProposal?: IssuerScopeProposal | null
  /** The document pane's find bar: the browser filters its table by it. */
  findTokens: FilterToken[]
  findSearch: string
  /** Clears the shared find expression above, unchanged, straight through to
   *  `ArticleBrowser`'s own filter chip. See that prop's own doc. */
  onClearFind?: () => void
  /** The browser header's search icon, see `ArticleBrowser.onFindRequest`. */
  onFindRequest?: () => void
  onClose: () => void
  /** The remainder path's landing callback: the mask jumps to the statement
   *  the status pick created. */
  onStatementCreated?: (framework: string, statementId: string) => void
}

/**
 * Hosts the article browser in place of the statement cards and wires it to
 * the writes: a statement's scope Apply is ONE `useUpdateStatementScope`
 * with the staged set (a tick is browser-local until then, same contract as
 * every scope write in this pane), the remainder's status pick is ONE
 * `useCreateStatement` with exactly the staged articles (a blanket
 * statement when they are the whole pool, since the server refuses an empty
 * explicit subset and a full one would only freeze today's pool).
 */
export function ArticleBrowserSheet({
  declId,
  framework,
  sheet,
  cards,
  pool,
  issuerProposal = null,
  findTokens,
  findSearch,
  onClearFind,
  onFindRequest,
  onClose,
  onStatementCreated,
}: ArticleBrowserSheetProps) {
  const { translate } = useTranslation()
  const updateScope = useUpdateStatementScope()
  const applyPool = useApplyStatementPoolScope()
  const createStatement = useCreateStatement()
  const poolNumbers = useMemo(() => pool.map((a) => a.articleNumber), [pool])
  const card =
    sheet.kind === "scope"
      ? cards.find((c) => c.id === sheet.cardId)
      : undefined

  const statements = useMemo<BrowserStatement[]>(
    () =>
      cards
        .filter((c) => !c.excluded)
        .map((c) => ({
          id: c.id,
          label: verdictSentence(c.verdict, translate),
          articleNumbers: c.scopeAll ? poolNumbers : c.articleNumbers,
          current: c.id === card?.id,
        })),
    [cards, poolNumbers, card?.id, translate],
  )
  const suggestions = useMemo(() => {
    if (!card) return []
    const issuer = issuerSuggestion(issuerProposal, translate, poolNumbers)
    const fromDocument = searchSuggestions(card, translate)
    return issuer ? [issuer, ...fromDocument] : fromDocument
  }, [card, issuerProposal, poolNumbers, translate])
  const rest = useMemo(
    () =>
      sheet.kind === "remainder" ? uncoveredArticleNumbers(cards, pool) : [],
    [sheet.kind, cards, pool],
  )

  if (sheet.kind === "remainder") {
    const create = async (staged: string[], verdict: Verdict) => {
      if (staged.length === 0) return
      const blanket = staged.length === poolNumbers.length
      try {
        const created = await createStatement.mutateAsync({
          declarationId: declId,
          framework,
          verdict,
          scopeAll: blanket,
          articleNumbers: blanket ? [] : staged,
          statementText: null,
          evidence: null,
        })
        toast.success(
          translate("MC_DECL_V2_REST_CREATED", {
            n: staged.length,
            status: translate(verdictLabelKey(verdict)),
          }),
        )
        onClose()
        onStatementCreated?.(created.framework, created.statementId)
      } catch {
        // `useCreateStatement`'s own onError already toasted the reason.
      }
    }
    return (
      <div className="sv2-sheet" data-testid="article-browser-sheet">
        <ArticleBrowser
          declId={declId}
          pool={pool}
          restrictTo={rest}
          applied={rest}
          statements={statements}
          tokens={findTokens}
          search={findSearch}
          onClearFind={onClearFind}
          onFindRequest={onFindRequest}
          initialView={sheet.view}
          current={
            <>
              <CountPill
                count={rest.length}
                total={pool.length}
                quiet
                translate={translate}
              />
              <span className="sv2-hw">
                {headVerb(rest.length, translate)}{" "}
                {translate("MC_DECL_V2_HEAD_VERDICT_PENDING")}
              </span>
            </>
          }
          onCancel={onClose}
          renderCommit={(staged) => (
            <VerdictSelect
              value={null}
              onChange={(v) => void create(staged, v)}
              disabled={staged.length === 0 || createStatement.isPending}
              triggerLabel={translate("MC_DECL_V2_BROWSER_REST_PICK_STATUS", {
                n: staged.length,
              })}
            />
          )}
        />
      </div>
    )
  }

  if (!card) return null

  return (
    <div className="sv2-sheet" data-testid="article-browser-sheet">
      <ArticleBrowser
        declId={declId}
        pool={pool}
        // A genuinely scope_all card carries a contractually EMPTY
        // `articleNumbers`; it starts from M2's resolution instead, so the
        // "everything currently covered" starting point is never lost.
        applied={card.scopeAll ? card.resolvedArticles : card.articleNumbers}
        suggestions={suggestions}
        suggestionsNote={
          issuerProposal && needsSeveralManufacturersHint(issuerProposal) ? (
            <SeveralManufacturersHint proposal={issuerProposal} />
          ) : undefined
        }
        statements={statements}
        tokens={findTokens}
        search={findSearch}
        onClearFind={onClearFind}
        onFindRequest={onFindRequest}
        geltung={sheet.geltung}
        initialView={sheet.view}
        current={
          <StatementSentence
            card={card}
            total={pool.length}
            translate={translate}
          />
        }
        onCancel={onClose}
        renderCommit={(staged, dirty) => (
          <button
            type="button"
            className={cn("sv2-hact", dirty && "sv2-hact-primary")}
            disabled={
              !dirty ||
              staged.length === 0 ||
              updateScope.isPending ||
              applyPool.isPending
            }
            onClick={() => {
              // The fork is about MEANING, not size: "everything" is sent as a
              // command the server resolves against the live pool (and which
              // refuses on an approved declaration), an explicit subset as a
              // list. The list route carries any pool this screen can show
              // (`MAX_SCOPE_ARTICLE_NUMBERS`), so unticking one article of
              // 10,582 is an ordinary save — it used to 422 (prod 2026-10-28).
              const stagedSet = new Set(staged)
              const wholePool =
                staged.length === poolNumbers.length &&
                poolNumbers.every((n) => stagedSet.has(n))
              if (wholePool) {
                applyPool.mutate(
                  {
                    declarationId: declId,
                    statementId: card.id,
                    poolNumbers: staged,
                  },
                  { onSuccess: onClose },
                )
              } else {
                updateScope.mutate(
                  {
                    declarationId: declId,
                    statementId: card.id,
                    articleNumbers: staged,
                  },
                  { onSuccess: onClose },
                )
              }
            }}
          >
            {translate("MC_DECL_V2_BROWSER_APPLY", { n: staged.length })}
          </button>
        )}
      />
    </div>
  )
}
